import React from 'react';
import { Settings, Schedule, Message, Apps, Computer } from '@material-ui/icons';
import { AdminConsoleAdminsPanel } from '../admin-console-content-pages/admin-console-admins/AdminConsoleAdminsPanel';
import { AdminConsoleOnboardingApplicationsPanel } from '../admin-console-content-pages/admin-console-onboarding-applications/AdminConsoleOnboardingApplicationsPanel';
import { AdminConsoleBulletinSettings } from '../admin-console-content-pages/admin-console-bulletin-settings/AdminConsoleBulletinSettings';
import { AdminConsoleIncidentReportingPanel } from '../admin-console-content-pages/admin-console-incident-reporting/AdminConsoleIncidentReportingPanel';
import { AdminConsoleOperationsPanel } from '../admin-console-content-pages/admin-console-operations/AdminConsoleOperationsPanel';
import { AdminConsoleFallbackPage } from '../admin-console-content-pages/AdminConsoleFallbackPage';
import { AdminConsoleResourcesPanel } from '../admin-console-content-pages/admin-console-resources/AdminConsoleResourcesPanel';
import { AdminConsoleRoles } from '../admin-console-content-pages/admin-console-roles/AdminConsoleRoles';
import { AdminConsoleTags } from '../admin-console-content-pages/admin-console-tags/AdminConsoleTags';
import { AdminConsoleCommunicationPreferences } from '../Communications/Preferences/AdminConsoleCommunicationPreferences';
import { AdminConsoleCommunicationTemplate } from '../Communications/Templates/AdminConsoleCommunicationTemplate';
import { AdminConsoleCommunicationReminders } from '../Communications/Reminders/AdminConsoleCommunicationReminders';
import { AdminConsoleCommunicationNotifications } from '../Communications/Notifications/AdminConsoleCommunicationNotifications';
import { AdminConsoleEmbeddableWidgetsPanel } from '../admin-console-content-pages/Integrations/EmbeddableWidgets/AdminConsoleEmbeddableWidgetsPanel';
import { AdminConsoleAppsScreen } from '../admin-console-content-pages/Integrations/Apps/AdminConsoleAppsScreen';
import { useOrgInfo } from '../../../../global/auth/use-org-info/use-org-info';
import { AdminConsoleActivityReportingPanel } from '../admin-console-content-pages/admin-console-activity-reporting/AdminConsoleActivityReportingPanel';
import { AdminConsoleSecurityPanel } from '../admin-console-content-pages/admins-console-security/AdminConsoleSecurityPanel';
import { AdminConsoleBilling } from '../admin-console-content-pages/admin-console-billing/AdminConsoleBilling';
import { AdminConsoleFunnels } from '../admin-console-content-pages/admin-console-funnels/AdminConsoleFunnels';

interface Route {
  label: string;
  route: string;
  component: React.ElementType;
  enabled?: boolean;
}
interface RouteGroups {
  heading: string;
  icon: React.ElementType;
  routeOptions: Route[];
}

interface RoutesProps {
  routeGroups: RouteGroups[];
}

export const useAdminConsoleRoutesMap = (route: string): RoutesProps => {
  const orgInfo = useOrgInfo();

  return {
    routeGroups: [
      {
        heading: 'System',
        icon: Computer,
        routeOptions: [
          {
            label: 'Admins',
            route: `${route}/admins`,
            component: AdminConsoleAdminsPanel,
          },
          {
            label: 'Billing',
            route: `${route}/billing`,
            component: AdminConsoleBilling,
          },
          {
            label: 'Security',
            route: `${route}/security`,
            component: AdminConsoleSecurityPanel,
          },
        ],
      },
      {
        heading: 'General',
        icon: Settings,
        routeOptions: [
          {
            label: 'Onboarding',
            route: `${route}/onboarding`,
            component: AdminConsoleOnboardingApplicationsPanel,
          },
          {
            label: 'Funnels',
            route: `${route}/funnels`,
            component: AdminConsoleFunnels,
          },
          {
            label: 'Bulletins',
            route: `${route}/bulletins`,
            component: AdminConsoleBulletinSettings,
            enabled: orgInfo?.packageConfig ? orgInfo.packageConfig.bulletins : true
          },
          {
            label: 'Resources',
            route: `${route}/resources`,
            component: AdminConsoleResourcesPanel,
          },
        ],
      },
      {
        heading: 'Activities & Rosters',
        icon: Schedule,
        routeOptions: [
          {
            label: 'Operations',
            route: `${route}/operations`,
            component: AdminConsoleOperationsPanel,
            enabled: orgInfo?.packageConfig ? orgInfo.packageConfig.rostering : true
          },
          {
            label: 'Activity Reporting',
            route: `${route}/reporting`,
            component: AdminConsoleActivityReportingPanel,
            enabled: orgInfo?.packageConfig ? orgInfo.packageConfig.rostering : true
          },
          {
            label: 'Activity Tags',
            route: `${route}/tags`,
            component: AdminConsoleTags,
            enabled: orgInfo?.packageConfig ? orgInfo.packageConfig.rostering : true
          },
          {
            label: 'Incident Reporting',
            route: `${route}/incident-reporting`,
            component: AdminConsoleIncidentReportingPanel,
            enabled: orgInfo?.packageConfig ? orgInfo.packageConfig.incidents : true
          },
          {
            label: 'Roles',
            route: `${route}/roles`,
            component: AdminConsoleRoles,
          },
        ],
      },
      {
        heading: 'Communications',
        icon: Message,
        routeOptions: [
          {
            label: 'Preferences',
            route: `${route}/preferences`,
            component: AdminConsoleCommunicationPreferences,
          },
          {
            label: 'Templates',
            route: `${route}/templates`,
            component: AdminConsoleCommunicationTemplate,
          },
          {
            label: 'Reminders & Automations',
            route: `${route}/reminders`,
            component: AdminConsoleCommunicationReminders,
          },
          {
            label: 'Notifications & Alerts',
            route: `${route}/notifications`,
            component: AdminConsoleCommunicationNotifications,
          },
        ],
      },
      {
        heading: 'Integrations',
        icon: Apps,
        routeOptions: [
          {
            label: 'Apps',
            route: `${route}/apps`,
            component: AdminConsoleAppsScreen,
            enabled: orgInfo?.packageConfig ? orgInfo.packageConfig.integrations : true
          },
          {
            label: 'Embeddable Widgets',
            route: `${route}/embeddable-widgets`,
            component: AdminConsoleEmbeddableWidgetsPanel,
            enabled: orgInfo?.packageConfig ? orgInfo.packageConfig.integrations : true
          },
        ],
      },
      {
        heading: 'Fallback',
        icon: Settings,
        routeOptions: [
          {
            label: '',
            route: `${route}`,
            component: AdminConsoleFallbackPage,
          },
        ],
      },
    ],
  }
};

export const adminConsoleFallbackPath = 'admins';
